import { motion } from "motion/react";
import { EASE, SPRING } from "@/components/motion/motion";
import { usePlayOnce } from "@/hooks/use-play-once";
import {
	COSTS,
	fillTemplate,
	formatEuro,
	portalPaybackMonth,
	portalRevenue,
} from "@/lib/costs";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import { THRESHOLD, TRENDS } from "./scenes/CleaningScene";
import { formatPercent } from "./scenes/StageScene";

const INSTANT = { duration: 0 } as const;

/**
 * QHSE follow-up portal sold as a subscription: cumulative revenue month by
 * month rises past the cost of the first version. Bars grow once in view;
 * server and reduced motion render the finished chart.
 */
export function PortalRevenue({ className }: { className?: string }) {
	const { dict, locale } = useI18n();
	const copy = dict.useCases.items.qhse.why.visual;
	const { ref, rewound } = usePlayOnce<HTMLElement>();
	const revenue = portalRevenue();
	const cost = COSTS.businessApp.build.after;
	const payback = portalPaybackMonth();
	const max = Math.max(cost, ...revenue) * 1.08;
	const euro = (value: number) => formatEuro(value, locale);
	const { monthlyPerClient, newClientsPerMonth } = COSTS.useCases.qhse;

	return (
		<figure
			ref={ref}
			className={cn("rounded-3xl border bg-card p-6 md:p-8", className)}
		>
			<figcaption className="font-mono text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
				{copy.title}
			</figcaption>
			<p className="sr-only">
				{fillTemplate(copy.summary, {
					month: payback,
					total: euro(revenue[revenue.length - 1] ?? 0),
					cost: euro(cost),
				})}
			</p>
			<div aria-hidden="true" className="mt-8">
				<div className="relative h-52 sm:h-60">
					<div
						className="absolute inset-x-0 z-10 border-t border-dashed border-foreground/50"
						style={{ bottom: `${(cost / max) * 100}%` }}
					>
						<span className="absolute -top-6 left-0 rounded-full border bg-background px-2 py-0.5 font-mono text-[10px] whitespace-nowrap">
							{copy.cost} · {euro(cost)}
						</span>
					</div>
					<div className="absolute inset-0 grid grid-cols-12 items-end gap-1.5 sm:gap-2">
						{revenue.map((total, index) => {
							const paid = index + 1 >= payback;
							return (
								<motion.span
									key={`m${index + 1}`}
									className={cn(
										"block origin-bottom rounded-t-md",
										paid ? "bg-signal" : "bg-foreground/20",
									)}
									style={{ height: `${(total / max) * 100}%` }}
									initial={false}
									animate={{ scaleY: rewound ? 0 : 1 }}
									transition={
										rewound
											? INSTANT
											: { duration: 0.7, ease: EASE, delay: 0.2 + index * 0.08 }
									}
								/>
							);
						})}
					</div>
				</div>
				<div className="mt-2 grid grid-cols-12 gap-1.5 sm:gap-2">
					{revenue.map((_, index) => (
						<span
							key={`l${index + 1}`}
							className="text-center font-mono text-[10px] text-muted-foreground tabular-nums"
						>
							{index + 1}
						</span>
					))}
				</div>
				<p className="mt-1 text-right font-mono text-[10px] text-muted-foreground">
					{copy.months}
				</p>
			</div>
			<div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
				<span className="flex items-center gap-2 text-sm">
					<span className="h-2 w-4 rounded-full bg-signal" />
					{copy.revenue}
				</span>
				<motion.span
					className="rounded-full bg-signal px-3 py-1 font-mono text-xs font-semibold text-signal-foreground"
					initial={false}
					animate={
						rewound ? { opacity: 0, scale: 0.6 } : { opacity: 1, scale: 1 }
					}
					transition={
						rewound
							? INSTANT
							: { ...SPRING, delay: 0.2 + revenue.length * 0.08 }
					}
				>
					{fillTemplate(copy.payback, { month: payback })}
				</motion.span>
			</div>
			<p className="mt-4 text-xs text-pretty text-muted-foreground">
				{fillTemplate(copy.note, {
					price: euro(monthlyPerClient),
					clients: newClientsPerMonth,
				})}
			</p>
		</figure>
	);
}

const SERIES = TRENDS.a;
const FLOOR = 85;
const CEILING = 100;
const toY = (value: number) => ((CEILING - value) / (CEILING - FLOOR)) * 100;
const TREND_PATH = SERIES.map(
	(value, index) =>
		`${index === 0 ? "M" : "L"}${(index / (SERIES.length - 1)) * 600} ${toY(value) * 3}`,
).join(" ");

/**
 * Commercial cleaning: twelve months of quality scores against the
 * contractual threshold, the record that defends a contract at renewal.
 * The line draws once in view; server and reduced motion render it drawn.
 */
export function QualityTrend({ className }: { className?: string }) {
	const { dict, locale } = useI18n();
	const copy = dict.useCases.items.cleaning.why.visual;
	const { ref, rewound } = usePlayOnce<HTMLElement>();
	const average =
		Math.round(
			(SERIES.reduce((sum, value) => sum + value, 0) / SERIES.length) * 10,
		) / 10;
	const months = copy.months.split(" ");
	const stats = [
		{
			key: "average",
			label: copy.average,
			value: formatPercent(average, locale),
		},
		{ key: "closing", label: copy.closing, value: copy.closingValue },
		{ key: "controls", label: copy.controls, value: copy.controlsValue },
	];

	return (
		<figure
			ref={ref}
			className={cn("rounded-3xl border bg-card p-6 md:p-8", className)}
		>
			<figcaption className="font-mono text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
				{copy.title}
			</figcaption>
			<p className="mt-2 text-sm font-semibold">{copy.series}</p>
			<div aria-hidden="true" className="mt-6 pl-10">
				<div className="relative h-48 sm:h-56">
					{[85, 90, 95, 100].map((value) => (
						<div
							key={value}
							className={cn(
								"absolute inset-x-0 border-t",
								value === THRESHOLD
									? "border-dashed border-foreground/50"
									: "border-dashed",
							)}
							style={{ top: `${toY(value)}%` }}
						>
							<span className="absolute right-full mr-3 -translate-y-1/2 font-mono text-[10px] text-muted-foreground tabular-nums">
								{formatPercent(value, locale)}
							</span>
						</div>
					))}
					<span
						className="absolute right-0 -translate-y-[130%] rounded-full border bg-background px-2 py-0.5 font-mono text-[10px]"
						style={{ top: `${toY(THRESHOLD)}%` }}
					>
						{fillTemplate(copy.threshold, {
							value: formatPercent(THRESHOLD, locale),
						})}
					</span>
					<svg
						aria-hidden="true"
						viewBox="0 0 600 300"
						preserveAspectRatio="none"
						className="absolute inset-0 size-full overflow-visible"
					>
						<motion.path
							d={TREND_PATH}
							fill="none"
							className="stroke-signal"
							strokeWidth={3}
							strokeLinecap="round"
							strokeLinejoin="round"
							vectorEffect="non-scaling-stroke"
							initial={false}
							animate={{ pathLength: rewound ? 0 : 1 }}
							transition={
								rewound ? INSTANT : { duration: 1.8, ease: EASE, delay: 0.2 }
							}
						/>
					</svg>
					{SERIES.map((value, index) => (
						<motion.span
							key={`p${index + 1}`}
							className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-signal"
							style={{
								left: `${(index / (SERIES.length - 1)) * 100}%`,
								top: `${toY(value)}%`,
							}}
							initial={false}
							animate={{ scale: rewound ? 0 : 1 }}
							transition={
								rewound
									? INSTANT
									: { ...SPRING, delay: 0.2 + (index / SERIES.length) * 1.8 }
							}
						/>
					))}
				</div>
				<div className="relative mt-2 h-4">
					{months.map((month, index) => (
						<span
							key={`${month}-${index + 1}`}
							className="absolute -translate-x-1/2 font-mono text-[10px] text-muted-foreground"
							style={{ left: `${(index / (months.length - 1)) * 100}%` }}
						>
							{month}
						</span>
					))}
				</div>
			</div>
			<dl className="mt-6 grid gap-4 border-t pt-5 sm:grid-cols-3">
				{stats.map((stat) => (
					<div key={stat.key}>
						<dt className="text-xs text-muted-foreground">{stat.label}</dt>
						<dd className="mt-1 font-mono text-lg font-semibold">
							{stat.value}
						</dd>
					</div>
				))}
			</dl>
			<p className="mt-4 text-xs text-pretty text-muted-foreground">
				{copy.note}
			</p>
		</figure>
	);
}
